import {
  MODULE_ON_INPUT_CHANGE_CONTROL_KEY,
  type MODULE_PREFIX,
  MODULE_TYPE,
  type Module,
} from "ee/constants/ModuleConstants";
import type { ModulesReducerState } from "ee/reducers/entityReducers/modulesReducer";
import type { PackagesReducerState } from "ee/reducers/entityReducers/packagesReducer";
import type {
  ModuleId,
  ModuleInstance,
} from "ee/constants/ModuleInstanceConstants";
import { getNextEntityName } from "utils/AppsmithUtils";
import { migrateDSL, type NestedDSL } from "@shared/dsl";
import { isDynamicValue, type DynamicPath } from "utils/DynamicBindingUtils";
import { objectKeys } from "@appsmith/utils";
import type { WidgetProps } from "@shared/dsl/src/migrate/types";
import { omit, uniqBy } from "lodash";

interface ExportPackageAsJSONFileProps {
  packageId: string;
  onSuccessCb?: () => void;
}

export const createNewModuleName = (
  allModules: ModulesReducerState,
  prefix: MODULE_PREFIX,
) => {
  const modules: Module[] = convertModulesToArray(allModules);
  const names = modules.map((q) => q.name);

  return getNextEntityName(`${prefix}Module`, names);
};

export const convertModulesToArray = (modules: ModulesReducerState) => {
  return Object.values(modules).map((module) => module);
};

export const selectAllQueryModules = (modules: Module[]) => {
  const queryModules = modules.filter(
    (module) => module.type === MODULE_TYPE.QUERY,
  );

  return queryModules;
};

export const selectAllJSModules = (modules: Module[]) => {
  const jsModules = modules.filter((module) => module.type === MODULE_TYPE.JS);

  return jsModules;
};

export const getModuleIdPackageNameMap = (
  modules: Module[],
  packages: PackagesReducerState,
) => {
  const modulePackageMap: Record<ModuleId, string> = {};

  modules.forEach((module) => {
    const pkgId = module.packageId;

    modulePackageMap[module.id] = packages[pkgId]?.name || "";
  });

  return modulePackageMap;
};

export const getExportPackageAPIRoute = (
  packageId: string,
  branchName?: string,
) => {
  let exportUrl = `/api/v1/packages/export/${packageId}`;

  if (branchName) {
    exportUrl += `?branchName=${branchName}`;
  }

  return exportUrl;
};

export const exportPackageAsJSONFile = ({
  onSuccessCb,
  packageId,
}: ExportPackageAsJSONFileProps) => {
  // export api response comes with content-disposition header.
  // there is no straightforward way to handle it with axios/fetch
  const id = `t--export-app-link`;
  const existingLink = document.getElementById(id);

  existingLink && existingLink.remove();
  const link = document.createElement("a");

  link.href = getExportPackageAPIRoute(packageId);
  link.id = id;
  document.body.appendChild(link);

  // @ts-expect-error: Types are not available
  if (!window.Cypress) {
    link.click();
  }

  onSuccessCb?.();
};

export function getNextPatchVersion(currentVersion: string) {
  // Split the current version string into an array of [MAJOR, MINOR, PATCH]
  const versionParts = currentVersion.split(".");

  // Increment the patch version (last part of the array)
  versionParts[2] = (parseInt(versionParts[2]) + 1).toString();

  // Join the version parts back together and return the next patch version
  return versionParts.join(".");
}

export async function migrateUIModuleInstanceLayout_mutate(
  moduleInstances: ModuleInstance<{ dsl: NestedDSL<unknown> }>[] = [],
) {
  for (const moduleInstance of moduleInstances) {
    if (moduleInstance.type === MODULE_TYPE.UI) {
      const currentDSL = moduleInstance?.layouts?.[0]?.dsl;

      if (currentDSL) {
        await migrateDSL(currentDSL);
      }
    }
  }
}

export function calculateDynamicBindingPathListForOutputsForm(
  outputsForm: Module["outputsForm"],
  existingDynamicBindingPathList: DynamicPath[] = [],
): DynamicPath[] | undefined {
  const dynamicBindingPathList = existingDynamicBindingPathList.filter(
    (path) => !path.key.startsWith("outputsForm"),
  );

  outputsForm.forEach((section, index) => {
    section.children?.forEach((property, propertyIndex) => {
      if (isDynamicValue(property.value)) {
        dynamicBindingPathList.push({
          key: `outputsForm[${index}].children[${propertyIndex}].value`,
        });
      }
    });
  });

  return dynamicBindingPathList.length > 0 ? dynamicBindingPathList : undefined;
}

export function calculateDynamicTriggerPathListForOnInputChange(
  onInputChange: Module["onInputChange"],
  prefix: string = "",
) {
  const dynamicTriggerPathList: DynamicPath[] = [];

  Object.entries(onInputChange || {}).forEach(([key, value]) => {
    if (isDynamicValue(value)) {
      dynamicTriggerPathList.push({
        key: `${prefix}onInputChange.${key}`,
      });
    }
  });

  return dynamicTriggerPathList.length > 0 ? dynamicTriggerPathList : undefined;
}

export function appendPrefixToDynamicPropertyPathList(
  newDynamicPropertyPathList: DynamicPath[] | undefined,
  existingDynamicPropertyPathList: DynamicPath[] | undefined,
  prefix: string = "",
) {
  if (!prefix) {
    return uniqBy(
      [
        ...(existingDynamicPropertyPathList || []),
        ...(newDynamicPropertyPathList || []),
      ],
      "key",
    );
  }

  const newDynamicPropertyPathListWithPrefix = (
    newDynamicPropertyPathList || []
  ).map((path) => {
    return {
      key: `${prefix}${path.key}`,
    };
  });

  return uniqBy(
    [
      ...(existingDynamicPropertyPathList || []),
      ...newDynamicPropertyPathListWithPrefix,
    ],
    "key",
  );
}

export function removeOutputFormsDynamicBindingPathList(
  mainContainerWidget: WidgetProps,
) {
  if (mainContainerWidget.dynamicBindingPathList) {
    mainContainerWidget.dynamicBindingPathList = (
      mainContainerWidget.dynamicBindingPathList as DynamicPath[]
    ).filter((path) => !path.key.startsWith("outputsForm"));
  }
}

export function extractOnInputChangePropertiesFromWidget(widget: WidgetProps) {
  const dynamicTriggerPathList = widget.dynamicTriggerPathList
    ?.filter((path: DynamicPath) => path.key.startsWith("onInputChangeConfig"))
    .map((path: DynamicPath) => ({
      ...path,
      key: path.key.replace("onInputChangeConfig.", ""),
    })) as DynamicPath[];

  const dynamicPropertyPathList = widget.dynamicPropertyPathList
    ?.filter((path: DynamicPath) => path.key.startsWith("onInputChangeConfig"))
    .map((path: DynamicPath) => ({
      ...path,
      key: path.key.replace("onInputChangeConfig.", ""),
    })) as DynamicPath[];

  const onInputChange = omit(
    widget.onInputChangeConfig.onInputChange,
    MODULE_ON_INPUT_CHANGE_CONTROL_KEY,
  );

  return {
    onInputChange,
    dynamicTriggerPathList: dynamicTriggerPathList?.length
      ? dynamicTriggerPathList
      : undefined,
    dynamicPropertyPathList: dynamicPropertyPathList?.length
      ? dynamicPropertyPathList
      : undefined,
  };
}

export function removeOnInputChangeDynamicPropertyPathList(
  mainContainerWidget: WidgetProps,
) {
  if (mainContainerWidget?.dynamicPropertyPathList) {
    mainContainerWidget.dynamicPropertyPathList = (
      mainContainerWidget.dynamicPropertyPathList as DynamicPath[]
    ).filter((path) => !path.key.startsWith("onInputChangeConfig"));
  }

  if (!mainContainerWidget?.dynamicPropertyPathList?.length) {
    mainContainerWidget.dynamicPropertyPathList = undefined;
  }
}

export function removeOnInputChangeDynamicTriggerPathList(
  mainContainerWidget: WidgetProps,
) {
  if (mainContainerWidget?.dynamicTriggerPathList) {
    mainContainerWidget.dynamicTriggerPathList = (
      mainContainerWidget.dynamicTriggerPathList as DynamicPath[]
    ).filter((path) => !path.key.startsWith("onInputChangeConfig"));
  }

  if (!mainContainerWidget?.dynamicTriggerPathList?.length) {
    mainContainerWidget.dynamicTriggerPathList = undefined;
  }
}

export function calculateDynamicPropertyPathListForOnInputChange(
  dynamicPropertyPathList: DynamicPath[] | undefined,
  onInputChange: Module["onInputChange"],
) {
  const validPaths = objectKeys(onInputChange || {}).map(
    (key) => `onInputChange.${key}`,
  );

  const filteredPaths = (dynamicPropertyPathList || []).filter((path) =>
    validPaths.includes(path.key),
  );

  return filteredPaths.length > 0 ? filteredPaths : undefined;
}

export function sanitizeOnInputChange(
  inputsForm: Module["inputsForm"],
  onInputChange: Module["onInputChange"],
) {
  const inputLabels = inputsForm?.[0]?.children?.map((input) => input.label);

  const sanitizedOnInputChange = Object.entries(onInputChange || {}).reduce(
    (acc: Module["onInputChange"], [key, value]) => {
      if (acc && inputLabels?.includes(key) && Boolean(value)) {
        acc[key] = value;
      }

      return acc;
    },
    {} as Module["onInputChange"],
  );

  return objectKeys(sanitizedOnInputChange || {}).length
    ? sanitizedOnInputChange
    : undefined;
}
